import React, { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseUrl } from '../supabaseClient';
import '../styles/AdminPanel.css';

/* ════════════════════════════════════════════════════
   ADMIN PANEL — لوحة التحكم
   Connected to Supabase · Manages books, poems, videos,
   events, site content, and contact messages
   ════════════════════════════════════════════════════ */

const ACCENT_COLORS = [
  { name: 'ذهبي', color: '#C4A44A' },
  { name: 'أخضر', color: '#4A7C59' },
  { name: 'أحمر', color: '#7A3B2E' },
  { name: 'أزرق', color: '#4A7C8C' },
  { name: 'بني', color: '#A0522D' },
  { name: 'بنفسجي', color: '#9B4D6C' },
  { name: 'رمادي', color: '#6B7B8D' },
  { name: 'زيتي', color: '#2D6A4F' },
  { name: 'كاكاو', color: '#7A6B5B' },
  { name: 'عسلي', color: '#8B7355' },
];

const VIDEO_CATEGORIES = [
  { id: 'recitation', label: 'قراءات شعرية' },
  { id: 'interview', label: 'لقاءات ومقابلات' },
  { id: 'event', label: 'أمسيات وفعاليات' },
  { id: 'documentary', label: 'وثائقيات' },
];

const POEM_CATEGORIES = ['غزل', 'وطنية', 'مناسبات', 'نثر', 'وجدانيات'];

const EMOJI_OPTIONS = ['✒️', '🏛️', '📜', '⚖️', '🏢', '📚', '🌿', '🌍', '☕', '🎭', '🎤', '📰', '🏆', '🎓', '💼', '🕊️'];

export default function AdminPanel() {
  /* ── Auth State ─────────────────────────────────── */
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  /* ── Navigation ─────────────────────────────────── */
  const [activeView, setActiveView] = useState('dashboard');
  const [toast, setToast] = useState(null);

  /* ── Data ────────────────────────────────────────── */
  const [books, setBooks] = useState([]);
  const [munfaridat, setMunfaridat] = useState([]);
  const [videos, setVideos] = useState([]);
  const [bookPoems, setBookPoems] = useState([]);
  const [events, setEvents] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);

  /* ── Forms ───────────────────────────────────────── */
  const [editingBook, setEditingBook] = useState(null);
  const [editingPoem, setEditingPoem] = useState(null);
  const [editingMunfarida, setEditingMunfarida] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);

  /* ── Toast helper ────────────────────────────────── */
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* ════════════════════════════════════════════════════
     AUTH
     ════════════════════════════════════════════════════ */
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });

    if (error) {
      setLoginError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  /* ════════════════════════════════════════════════════
     DATA FETCHING
     ════════════════════════════════════════════════════ */
  const fetchAll = useCallback(async () => {
    const [booksRes, munRes, vidRes, poemsRes, eventsRes, contentRes, timelineRes, membershipsRes, messagesRes] = await Promise.all([
      supabase.from('books').select('*').order('sort_order'),
      supabase.from('munfaridat').select('*').order('sort_order'),
      supabase.from('videos').select('*').order('sort_order'),
      supabase.from('book_poems').select('*').order('sort_order'),
      supabase.from('events').select('*').order('event_date', { ascending: false }),
      supabase.from('site_content').select('*').eq('id', 1).single(),
      supabase.from('timeline').select('*').order('sort_order'),
      supabase.from('memberships').select('*').order('sort_order'),
      supabase.from('contact_messages').select('*').order('created_at', { ascending: false }),
    ]);

    if (booksRes.data) setBooks(booksRes.data);
    if (munRes.data) setMunfaridat(munRes.data);
    if (vidRes.data) setVideos(vidRes.data);
    if (poemsRes.data) setBookPoems(poemsRes.data);
    if (eventsRes.data) setEvents(eventsRes.data);
    if (contentRes.data) setSiteContent(contentRes.data);
    if (timelineRes.data) setTimeline(timelineRes.data);
    if (membershipsRes.data) setMemberships(membershipsRes.data);
    if (messagesRes.data) setContactMessages(messagesRes.data);
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  /* ════════════════════════════════════════════════════
     SITE CONTENT CRUD
     ════════════════════════════════════════════════════ */
  const saveSiteContent = async (data) => {
    const payload = {
      poet_name: data.poet_name,
      poet_title: data.poet_title,
      poet_title_en: data.poet_title_en,
      bio_paragraphs: data.bio_paragraphs || [],
      hero_quotes: data.hero_quotes || [],
      hero_image_url: data.hero_image_url || null,
      about_image_url: data.about_image_url || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('site_content').update(payload).eq('id', 1);
    if (error) {
      showToast('خطأ في حفظ المحتوى', 'error');
      return;
    }
    showToast('تم حفظ المحتوى بنجاح');
    fetchAll();
  };

  const uploadSiteImage = async (file, type) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `${type}-${Date.now()}.${ext}`;

    try {
      const { data: files } = await supabase.storage.from('site-images').list();
      const oldFiles = (files || []).filter(f => f.name.startsWith(`${type}-`));
      if (oldFiles.length > 0) {
        await supabase.storage.from('site-images').remove(oldFiles.map(f => f.name));
      }
    } catch (_) {}

    const { error } = await supabase.storage.from('site-images').upload(fileName, file, {
      upsert: true,
      cacheControl: '3600',
    });

    if (error) {
      showToast('خطأ في رفع الصورة', 'error');
      setUploading(false);
      return null;
    }

    const url = `${supabaseUrl}/storage/v1/object/public/site-images/${fileName}`;

    const field = type === 'hero' ? 'hero_image_url' : 'about_image_url';
    await supabase.from('site_content').update({ [field]: url, updated_at: new Date().toISOString() }).eq('id', 1);

    showToast('تم رفع الصورة بنجاح');
    setUploading(false);
    fetchAll();
    return url;
  };

  /* ── Timeline CRUD ──────────────────────────────── */
  const saveTimelineItem = async (data) => {
    const payload = { year: data.year, event: data.event, sort_order: parseInt(data.sort_order) || 0 };
    let result;
    if (data.id) {
      result = await supabase.from('timeline').update(payload).eq('id', data.id);
    } else {
      result = await supabase.from('timeline').insert(payload);
    }
    if (result.error) { showToast('خطأ في الحفظ', 'error'); return; }
    showToast(data.id ? 'تم التحديث' : 'تم الإضافة');
    fetchAll();
  };

  const deleteTimelineItem = async (id) => {
    if (!window.confirm('حذف هذا الحدث؟')) return;
    await supabase.from('timeline').delete().eq('id', id);
    showToast('تم الحذف');
    fetchAll();
  };

  /* ── Memberships CRUD ───────────────────────────── */
  const saveMembership = async (data) => {
    const payload = { title: data.title, role: data.role, icon: data.icon || '📜', sort_order: parseInt(data.sort_order) || 0 };
    let result;
    if (data.id) {
      result = await supabase.from('memberships').update(payload).eq('id', data.id);
    } else {
      result = await supabase.from('memberships').insert(payload);
    }
    if (result.error) { showToast('خطأ في الحفظ', 'error'); return; }
    showToast(data.id ? 'تم التحديث' : 'تم الإضافة');
    fetchAll();
  };

  const deleteMembership = async (id) => {
    if (!window.confirm('حذف هذه العضوية؟')) return;
    await supabase.from('memberships').delete().eq('id', id);
    showToast('تم الحذف');
    fetchAll();
  };

  /* ════════════════════════════════════════════════════
     BOOK CRUD
     ════════════════════════════════════════════════════ */
  const saveBook = async (bookData) => {
    const payload = {
      title: bookData.title,
      year: parseInt(bookData.year) || null,
      type: bookData.type || 'ديوان شعري',
      description: bookData.description || '',
      accent: bookData.accent || '#C4A44A',
      is_latest: bookData.is_latest || false,
      sort_order: parseInt(bookData.sort_order) || 0,
      cover_url: bookData.cover_url || null,
      pdf_url: bookData.pdf_url || null,
      pdf_parts: bookData.pdf_parts || [],
      buy_link: bookData.buy_link || null,
      allow_full_read: bookData.allow_full_read === true,                                   // false = visitors see preview only
      preview_pages: Math.min(Math.max(parseInt(bookData.preview_pages) || 10, 1), 60),      // DB allows 1–60
    };

    let result;
    if (bookData.id) {
      result = await supabase.from('books').update(payload).eq('id', bookData.id);
    } else {
      result = await supabase.from('books').insert(payload);
    }

    if (result.error) { showToast('خطأ في حفظ الديوان', 'error'); return; }
    showToast(bookData.id ? 'تم تحديث الديوان' : 'تم إضافة الديوان');
    setEditingBook(null);
    fetchAll();
  };

  const deleteBook = async (id) => {
    if (!window.confirm('هل تريد حذف هذا الديوان وجميع قصائده؟')) return;
    await supabase.from('books').delete().eq('id', id);
    showToast('تم حذف الديوان');
    fetchAll();
  };

  const uploadCover = async (file, bookId) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `${bookId}.${ext}`;
    await supabase.storage.from('covers').remove([fileName]);
    const { error } = await supabase.storage.from('covers').upload(fileName, file, { upsert: true, cacheControl: '3600' });
    if (error) { showToast('خطأ في رفع الصورة', 'error'); setUploading(false); return null; }
    const url = `${supabaseUrl}/storage/v1/object/public/covers/${fileName}`;
    await supabase.from('books').update({ cover_url: url }).eq('id', bookId);
    showToast('تم رفع الغلاف');
    setUploading(false);
    fetchAll();
    return url;
  };

  const uploadPdf = async (file, fileId) => {
    if (!file) return null;
    setUploading(true);
    const fileName = `${fileId}.pdf`;
    const { error } = await supabase.storage.from('pdfs').upload(fileName, file, { upsert: true, cacheControl: '3600', contentType: 'application/pdf' });
    if (error) { showToast(`خطأ في رفع الملف: ${error.message}`, 'error'); setUploading(false); return null; }
    const url = `${supabaseUrl}/storage/v1/object/public/pdfs/${fileName}`;
    showToast('تم رفع ملف PDF');
    setUploading(false);
    return url;
  };

  /* ════════════════════════════════════════════════════
     BOOK POEMS CRUD
     ════════════════════════════════════════════════════ */
  const saveBookPoem = async (poemData) => {
    const payload = { book_id: poemData.book_id, title: poemData.title, category: poemData.category || 'غزل', verses: poemData.verses, sort_order: parseInt(poemData.sort_order) || 0 };
    let result;
    if (poemData.id) { result = await supabase.from('book_poems').update(payload).eq('id', poemData.id); }
    else { result = await supabase.from('book_poems').insert(payload); }
    if (result.error) { showToast('خطأ في حفظ القصيدة', 'error'); return; }
    showToast(poemData.id ? 'تم تحديث القصيدة' : 'تم إضافة القصيدة');
    setEditingPoem(null);
    fetchAll();
  };

  const deleteBookPoem = async (id) => {
    if (!window.confirm('حذف هذه القصيدة؟')) return;
    await supabase.from('book_poems').delete().eq('id', id);
    showToast('تم حذف القصيدة');
    fetchAll();
  };

  /* ════════════════════════════════════════════════════
     MUNFARIDAT CRUD
     ════════════════════════════════════════════════════ */
  const saveMunfarida = async (data) => {
    const payload = { title: data.title, category: data.category || 'غزل', verses: data.verses, sort_order: parseInt(data.sort_order) || 0 };
    let result;
    if (data.id) { result = await supabase.from('munfaridat').update(payload).eq('id', data.id); }
    else { result = await supabase.from('munfaridat').insert(payload); }
    if (result.error) { showToast('خطأ في حفظ المنفردة', 'error'); return; }
    showToast(data.id ? 'تم التحديث' : 'تم الإضافة');
    setEditingMunfarida(null);
    fetchAll();
  };

  const deleteMunfarida = async (id) => {
    if (!window.confirm('حذف هذه القصيدة المنفردة؟')) return;
    await supabase.from('munfaridat').delete().eq('id', id);
    showToast('تم الحذف');
    fetchAll();
  };

  /* ════════════════════════════════════════════════════
     VIDEOS CRUD
     ════════════════════════════════════════════════════ */
  const saveVideo = async (data) => {
    const payload = { title: data.title, description: data.description || '', category: data.category || 'recitation', youtube_id: data.youtube_id || null, video_type: data.video_type || 'youtube', video_url: data.video_url || null, duration: data.duration || '', year: parseInt(data.year) || null, is_featured: data.is_featured || false, sort_order: parseInt(data.sort_order) || 0 };
    let result;
    if (data.id) { result = await supabase.from('videos').update(payload).eq('id', data.id); }
    else { result = await supabase.from('videos').insert(payload); }
    if (result.error) { showToast('خطأ في حفظ الفيديو', 'error'); return; }
    showToast(data.id ? 'تم تحديث الفيديو' : 'تم إضافة الفيديو');
    setEditingVideo(null);
    fetchAll();
  };

  const deleteVideo = async (id) => {
    const video = videos.find(v => v.id === id);
    if (video?.video_type === 'uploaded' && video?.video_url) {
      const fileName = video.video_url.split('/').pop();
      await supabase.storage.from('videos').remove([fileName]);
    }
    if (!window.confirm('حذف هذا الفيديو؟')) return;
    await supabase.from('videos').delete().eq('id', id);
    showToast('تم حذف الفيديو');
    fetchAll();
  };

  const uploadVideo = async (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 100) { showToast(`الملف كبير جداً (${fileSizeMB.toFixed(1)}MB)`, 'error'); return null; }
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('videos').upload(fileName, file, { cacheControl: '3600', upsert: false });
    if (error) { showToast(`خطأ في رفع الفيديو: ${error.message}`, 'error'); setUploading(false); return null; }
    const url = `${supabaseUrl}/storage/v1/object/public/videos/${fileName}`;
    setUploading(false);
    showToast(`تم رفع الفيديو (${fileSizeMB.toFixed(1)}MB)`);
    return url;
  };

  const getStorageUsed = async () => {
    try {
      const { data: fileList } = await supabase.storage.from('videos').list();
      return (fileList || []).reduce((sum, f) => sum + (f.metadata?.size || 0), 0);
    } catch (_) { return 0; }
  };

  /* ════════════════════════════════════════════════════
     EVENTS CRUD
     ════════════════════════════════════════════════════ */
  const saveEvent = async (data) => {
    const payload = { title: data.title, description: data.description || '', event_date: data.event_date || null, location: data.location || '', city: data.city || '', country: data.country || 'لبنان', event_type: data.event_type || 'أمسية شعرية', image_url: data.image_url || null, is_upcoming: data.is_upcoming || false, sort_order: parseInt(data.sort_order) || 0 };
    let result;
    if (data.id) { result = await supabase.from('events').update(payload).eq('id', data.id); }
    else { result = await supabase.from('events').insert(payload); }
    if (result.error) { showToast('خطأ في حفظ الفعالية', 'error'); return; }
    showToast(data.id ? 'تم تحديث الفعالية' : 'تم إضافة الفعالية');
    setEditingEvent(null);
    fetchAll();
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('حذف هذه الفعالية؟')) return;
    await supabase.from('events').delete().eq('id', id);
    showToast('تم حذف الفعالية');
    fetchAll();
  };

  const uploadEventImage = async (file, eventId) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `${eventId}.${ext}`;
    const { error } = await supabase.storage.from('events').upload(fileName, file, { upsert: true, cacheControl: '3600' });
    if (error) { showToast('خطأ في رفع الصورة', 'error'); setUploading(false); return null; }
    const url = `${supabaseUrl}/storage/v1/object/public/events/${fileName}`;
    await supabase.from('events').update({ image_url: url }).eq('id', eventId);
    showToast('تم رفع الصورة');
    setUploading(false);
    fetchAll();
    return url;
  };

  /* ════════════════════════════════════════════════════
     CONTACT MESSAGES CRUD
     ════════════════════════════════════════════════════ */
  const toggleMessageRead = async (id, currentStatus) => {
    const { error } = await supabase.from('contact_messages').update({ is_read: !currentStatus }).eq('id', id);
    if (error) { showToast('خطأ في تحديث الرسالة', 'error'); return; }
    setContactMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: !currentStatus } : m));
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('حذف هذه الرسالة نهائياً؟')) return;
    const { error } = await supabase.from('contact_messages').delete().eq('id', id);
    if (error) { showToast('خطأ في حذف الرسالة', 'error'); return; }
    showToast('تم حذف الرسالة');
    setContactMessages(prev => prev.filter(m => m.id !== id));
  };

  const markAllRead = async () => {
    const unreadIds = contactMessages.filter(m => !m.is_read).map(m => m.id);
    if (unreadIds.length === 0) return;
    const { error } = await supabase.from('contact_messages').update({ is_read: true }).in('id', unreadIds);
    if (error) { showToast('خطأ في التحديث', 'error'); return; }
    showToast('تم تعليم الكل كمقروء');
    setContactMessages(prev => prev.map(m => ({ ...m, is_read: true })));
  };

  /* ════════════════════════════════════════════════════
     RENDER — AUTH LOADING
     ════════════════════════════════════════════════════ */
  if (authLoading) {
    return <div className="adm-full-page"><div className="adm-loading">جار التحميل...</div></div>;
  }

  /* ════════════════════════════════════════════════════
     RENDER — LOGIN SCREEN
     ════════════════════════════════════════════════════ */
  if (!user) {
    return (
      <div className="adm-full-page">
        <div className="adm-login-card">
          <div className="adm-login-icon">✒️</div>
          <h1 className="adm-login-title">لوحة التحكم</h1>
          <p className="adm-login-sub">وجدي منيف عبد الصمد</p>
          <form onSubmit={handleLogin} className="adm-login-form">
            <input type="email" placeholder="البريد الإلكتروني" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required dir="ltr" className="adm-input" />
            <input type="password" placeholder="كلمة المرور" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required dir="ltr" className="adm-input" />
            {loginError && <p className="adm-login-err">{loginError}</p>}
            <button type="submit" className="adm-btn-gold" disabled={loginLoading}>{loginLoading ? 'جار الدخول...' : 'تسجيل الدخول'}</button>
          </form>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     RENDER — MAIN DASHBOARD
     ════════════════════════════════════════════════════ */
  const totalPoems = bookPoems.length + munfaridat.length;
  const unreadCount = contactMessages.filter(m => !m.is_read).length;

  return (
    <div className="adm-layout" dir="rtl">
      {toast && <div className={`adm-toast adm-toast--${toast.type}`}>{toast.msg}</div>}

      {/* Sidebar */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar-brand"><span>✒️</span><span>لوحة التحكم</span></div>
        <nav className="adm-nav">
          <button className={`adm-nav-btn ${activeView === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveView('dashboard')}>
            <span>📊</span> الرئيسية
          </button>
          <button className={`adm-nav-btn ${activeView === 'site-content' ? 'active' : ''}`} onClick={() => setActiveView('site-content')}>
            <span>🖋️</span> السيرة
          </button>
          <button className={`adm-nav-btn ${activeView === 'books' ? 'active' : ''}`} onClick={() => setActiveView('books')}>
            <span>📚</span> الإصدارات <em>{books.length}</em>
          </button>
          <button className={`adm-nav-btn ${activeView === 'book-poems' ? 'active' : ''}`} onClick={() => setActiveView('book-poems')}>
            <span>📖</span> قصائد الدواوين <em>{bookPoems.length}</em>
          </button>
          <button className={`adm-nav-btn ${activeView === 'munfaridat' ? 'active' : ''}`} onClick={() => setActiveView('munfaridat')}>
            <span>📜</span> قصائد متنوعة <em>{munfaridat.length}</em>
          </button>
          <button className={`adm-nav-btn ${activeView === 'videos' ? 'active' : ''}`} onClick={() => setActiveView('videos')}>
            <span>🎬</span> مرئيات <em>{videos.length}</em>
          </button>
          <button className={`adm-nav-btn ${activeView === 'events' ? 'active' : ''}`} onClick={() => setActiveView('events')}>
            <span>📅</span> الفعاليات <em>{events.length}</em>
          </button>
          <button className={`adm-nav-btn ${activeView === 'messages' ? 'active' : ''}`} onClick={() => setActiveView('messages')}>
            <span>📬</span> الرسائل {unreadCount > 0 && <em className="adm-unread-badge">{unreadCount}</em>}
          </button>
        </nav>
        <button className="adm-logout-btn" onClick={handleLogout}>خروج ←</button>
      </aside>

      {/* Main Content */}
      <main className="adm-main">

        {/* ═══════════ DASHBOARD ═══════════ */}
        {activeView === 'dashboard' && (
          <div>
            <h2 className="adm-page-title">مرحباً بك</h2>
            <div className="adm-stats">
              <div className="adm-stat-card"><div className="adm-stat-num">{books.length}</div><div className="adm-stat-label">إصدار</div></div>
              <div className="adm-stat-card"><div className="adm-stat-num">{totalPoems}</div><div className="adm-stat-label">قصيدة</div></div>
              <div className="adm-stat-card"><div className="adm-stat-num">{videos.length}</div><div className="adm-stat-label">فيديو</div></div>
              <div className="adm-stat-card"><div className="adm-stat-num">{events.length}</div><div className="adm-stat-label">فعالية</div></div>
              <div className="adm-stat-card" onClick={() => setActiveView('messages')} style={{ cursor: 'pointer' }}>
                <div className="adm-stat-num">{unreadCount}</div>
                <div className="adm-stat-label">رسائل جديدة</div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ SITE CONTENT (السيرة) ═══════════ */}
        {activeView === 'site-content' && (
          <SiteContentView
            siteContent={siteContent}
            timeline={timeline}
            memberships={memberships}
            onSaveContent={saveSiteContent}
            onUploadImage={uploadSiteImage}
            onSaveTimeline={saveTimelineItem}
            onDeleteTimeline={deleteTimelineItem}
            onSaveMembership={saveMembership}
            onDeleteMembership={deleteMembership}
            uploading={uploading}
            showToast={showToast}
          />
        )}

        {/* ═══════════ BOOKS ═══════════ */}
        {activeView === 'books' && (
          <div>
            <div className="adm-header-row">
              <h2 className="adm-page-title">الإصدارات</h2>
              <button className="adm-btn-gold" onClick={() => setEditingBook({ title: '', year: '', type: 'ديوان شعري', description: '', accent: '#C4A44A', sort_order: books.length + 1 })}>＋ إضافة ديوان</button>
            </div>
            {editingBook && <BookForm book={editingBook} onSave={saveBook} onCancel={() => setEditingBook(null)} onUploadCover={uploadCover} onUploadPdf={uploadPdf} uploading={uploading} />}
            <div className="adm-list">
              {books.map(b => (
                <div key={b.id} className="adm-row">
                  <div className="adm-row-accent" style={{ background: b.accent }} />
                  {b.cover_url && <img src={b.cover_url} alt="" className="adm-row-thumb" />}
                  <div className="adm-row-info"><strong>{b.title}</strong><span>{b.year} · {b.type}</span></div>
                  <div className="adm-row-actions">
                    <button onClick={() => setEditingBook(b)}>✏️</button>
                    <button className="danger" onClick={() => deleteBook(b.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════ BOOK POEMS ═══════════ */}
        {activeView === 'book-poems' && (
          <div>
            <div className="adm-header-row">
              <h2 className="adm-page-title">قصائد الدواوين</h2>
              <button className="adm-btn-gold" onClick={() => setEditingPoem({ title: '', book_id: books[0]?.id || '', category: 'غزل', verses: '', sort_order: bookPoems.length + 1 })}>＋ إضافة قصيدة</button>
            </div>
            {editingPoem && <PoemForm poem={editingPoem} books={books} onSave={saveBookPoem} onCancel={() => setEditingPoem(null)} />}
            <div className="adm-list">
              {bookPoems.map(p => {
                const book = books.find(b => b.id === p.book_id);
                return (
                  <div key={p.id} className="adm-row">
                    <div className="adm-row-info"><strong>{p.title}</strong><span>{book?.title || '—'} · {p.category}</span></div>
                    <div className="adm-row-actions">
                      <button onClick={() => setEditingPoem(p)}>✏️</button>
                      <button className="danger" onClick={() => deleteBookPoem(p.id)}>🗑️</button>
                    </div>
                  </div>
                );
              })}
              {bookPoems.length === 0 && <div className="adm-empty">لا توجد قصائد في الدواوين بعد</div>}
            </div>
          </div>
        )}

        {/* ═══════════ MUNFARIDAT ═══════════ */}
        {activeView === 'munfaridat' && (
          <div>
            <div className="adm-header-row">
              <h2 className="adm-page-title">قصائد متنوعة</h2>
              <button className="adm-btn-gold" onClick={() => setEditingMunfarida({ title: '', category: 'غزل', verses: '', sort_order: munfaridat.length + 1 })}>＋ إضافة منفردة</button>
            </div>
            {editingMunfarida && <MunfaridaForm data={editingMunfarida} onSave={saveMunfarida} onCancel={() => setEditingMunfarida(null)} />}
            <div className="adm-list">
              {munfaridat.map(m => (
                <div key={m.id} className="adm-row">
                  <div className="adm-row-info"><strong>{m.title}</strong><span>{m.category} · {m.verses.split('\n').length} بيت</span></div>
                  <div className="adm-row-actions">
                    <button onClick={() => setEditingMunfarida(m)}>✏️</button>
                    <button className="danger" onClick={() => deleteMunfarida(m.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════ VIDEOS ═══════════ */}
        {activeView === 'videos' && (
          <div>
            <div className="adm-header-row">
              <h2 className="adm-page-title">المرئيات</h2>
              <button className="adm-btn-gold" onClick={() => setEditingVideo({ title: '', description: '', category: 'recitation', youtube_id: '', duration: '', year: '', sort_order: videos.length + 1 })}>＋ إضافة فيديو</button>
            </div>
            {editingVideo && <VideoForm data={editingVideo} onSave={saveVideo} onCancel={() => setEditingVideo(null)} onUploadVideo={uploadVideo} uploading={uploading} getStorageUsed={getStorageUsed} />}
            <div className="adm-list">
              {videos.map(v => (
                <div key={v.id} className="adm-row">
                  {v.youtube_id && <img src={`https://img.youtube.com/vi/${v.youtube_id}/default.jpg`} alt="" className="adm-row-thumb" />}
                  <div className="adm-row-info"><strong>{v.title}</strong><span>{VIDEO_CATEGORIES.find(c => c.id === v.category)?.label} · {v.year}</span></div>
                  <div className="adm-row-actions">
                    <button onClick={() => setEditingVideo(v)}>✏️</button>
                    <button className="danger" onClick={() => deleteVideo(v.id)}>🗑️</button>
                  </div>
                </div>
              ))}
              {videos.length === 0 && <div className="adm-empty">لا توجد فيديوهات بعد</div>}
            </div>
          </div>
        )}

        {/* ═══════════ EVENTS ═══════════ */}
        {activeView === 'events' && (
          <div>
            <div className="adm-header-row">
              <h2 className="adm-page-title">الفعاليات</h2>
              <button className="adm-btn-gold" onClick={() => setEditingEvent({ title: '', description: '', event_date: '', location: '', city: '', country: 'لبنان', event_type: 'أمسية شعرية', is_upcoming: false, sort_order: events.length + 1 })}>＋ إضافة فعالية</button>
            </div>
            {editingEvent && <EventForm data={editingEvent} onSave={saveEvent} onCancel={() => setEditingEvent(null)} onUploadImage={uploadEventImage} uploading={uploading} />}
            <div className="adm-list">
              {events.map(e => (
                <div key={e.id} className="adm-row">
                  {e.image_url && <img src={e.image_url} alt="" className="adm-row-thumb" />}
                  <div className="adm-row-info"><strong>{e.is_upcoming ? '🟢 ' : ''}{e.title}</strong><span>{e.event_date} · {e.city}، {e.country} · {e.event_type}</span></div>
                  <div className="adm-row-actions">
                    <button onClick={() => setEditingEvent(e)}>✏️</button>
                    <button className="danger" onClick={() => deleteEvent(e.id)}>🗑️</button>
                  </div>
                </div>
              ))}
              {events.length === 0 && <div className="adm-empty">لا توجد فعاليات بعد</div>}
            </div>
          </div>
        )}

        {/* ═══════════ MESSAGES ═══════════ */}
        {activeView === 'messages' && (
          <MessagesView
            messages={contactMessages}
            onToggleRead={toggleMessageRead}
            onDelete={deleteMessage}
            onMarkAllRead={markAllRead}
          />
        )}

      </main>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   MESSAGES VIEW — الرسائل
   ════════════════════════════════════════════════════ */
function MessagesView({ messages, onToggleRead, onDelete, onMarkAllRead }) {
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unread | read

  const filtered = messages.filter(m => {
    if (filter === 'unread') return !m.is_read;
    if (filter === 'read') return m.is_read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.is_read).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    return d.toLocaleDateString('ar-LB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleRowClick = (msg) => {
    const newId = expandedId === msg.id ? null : msg.id;
    setExpandedId(newId);
    // Auto-mark as read when expanding
    if (newId && !msg.is_read) {
      onToggleRead(msg.id, false);
    }
  };

  return (
    <div>
      <div className="adm-header-row">
        <h2 className="adm-page-title">الرسائل {unreadCount > 0 && <span className="adm-msg-count">({unreadCount} جديدة)</span>}</h2>
        {unreadCount > 0 && (
          <button className="adm-btn-ghost" onClick={onMarkAllRead}>✓ تعليم الكل كمقروء</button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="adm-sub-tabs" style={{ marginBottom: '1rem' }}>
        <button className={`adm-sub-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          الكل ({messages.length})
        </button>
        <button className={`adm-sub-tab ${filter === 'unread' ? 'active' : ''}`} onClick={() => setFilter('unread')}>
          غير مقروءة ({unreadCount})
        </button>
        <button className={`adm-sub-tab ${filter === 'read' ? 'active' : ''}`} onClick={() => setFilter('read')}>
          مقروءة ({messages.length - unreadCount})
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="adm-empty">
          {filter === 'unread' ? 'لا توجد رسائل جديدة' : filter === 'read' ? 'لا توجد رسائل مقروءة' : 'لا توجد رسائل بعد'}
        </div>
      ) : (
        <div className="adm-list">
          {filtered.map(msg => (
            <div key={msg.id} className={`adm-msg-card ${!msg.is_read ? 'adm-msg-unread' : ''} ${expandedId === msg.id ? 'adm-msg-expanded' : ''}`}>
              {/* Header row — clickable */}
              <div className="adm-msg-header" onClick={() => handleRowClick(msg)}>
                <div className="adm-msg-indicator">{!msg.is_read && <span className="adm-msg-dot" />}</div>
                <div className="adm-msg-summary">
                  <div className="adm-msg-top-row">
                    <strong className="adm-msg-sender">{msg.name}</strong>
                    <span className="adm-msg-time">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="adm-msg-subject">{msg.subject || '(بدون موضوع)'}</div>
                  {expandedId !== msg.id && (
                    <div className="adm-msg-preview">{msg.message.substring(0, 100)}{msg.message.length > 100 ? '...' : ''}</div>
                  )}
                </div>
              </div>

              {/* Expanded content */}
              {expandedId === msg.id && (
                <div className="adm-msg-body">
                  <div className="adm-msg-meta">
                    <span>📧 {msg.email}</span>
                    <span>📅 {new Date(msg.created_at).toLocaleString('ar-LB', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="adm-msg-text">{msg.message}</div>
                  <div className="adm-msg-actions">
                    <a href={`mailto:${msg.email}?subject=رد: ${msg.subject || ''}`} className="adm-btn-gold" style={{ textDecoration: 'none', display: 'inline-block', fontSize: '.82rem', padding: '.5rem 1rem' }}>↩ الرد بالبريد</a>
                    <button className="adm-btn-ghost" onClick={(e) => { e.stopPropagation(); onToggleRead(msg.id, msg.is_read); }}>
                      {msg.is_read ? '📩 تعليم كغير مقروء' : '✓ تعليم كمقروء'}
                    </button>
                    <button className="adm-btn-ghost" style={{ color: '#e74c3c' }} onClick={(e) => { e.stopPropagation(); onDelete(msg.id); }}>
                      🗑️ حذف
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════
   SITE CONTENT VIEW — السيرة
   ════════════════════════════════════════════════════ */
function SiteContentView({ siteContent, timeline, memberships, onSaveContent, onUploadImage, onSaveTimeline, onDeleteTimeline, onSaveMembership, onDeleteMembership, uploading }) {
  const [subTab, setSubTab] = useState('info');
  const [form, setForm] = useState(null);
  const [editingTimeItem, setEditingTimeItem] = useState(null);
  const [editingMemberItem, setEditingMemberItem] = useState(null);

  useEffect(() => {
    if (siteContent) {
      setForm({ ...siteContent });
    }
  }, [siteContent]);

  if (!form) return <div className="adm-loading">جار التحميل...</div>;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const updateParagraph = (idx, val) => {
    const updated = [...(form.bio_paragraphs || [])];
    updated[idx] = val;
    set('bio_paragraphs', updated);
  };
  const addParagraph = () => set('bio_paragraphs', [...(form.bio_paragraphs || []), '']);
  const removeParagraph = (idx) => set('bio_paragraphs', (form.bio_paragraphs || []).filter((_, i) => i !== idx));

  const updateQuote = (idx, val) => {
    const updated = [...(form.hero_quotes || [])];
    updated[idx] = val;
    set('hero_quotes', updated);
  };
  const addQuote = () => set('hero_quotes', [...(form.hero_quotes || []), '']);
  const removeQuote = (idx) => set('hero_quotes', (form.hero_quotes || []).filter((_, i) => i !== idx));

  return (
    <div>
      <h2 className="adm-page-title">السيرة والمحتوى</h2>

      <div className="adm-sub-tabs">
        <button className={`adm-sub-tab ${subTab === 'info' ? 'active' : ''}`} onClick={() => setSubTab('info')}>
          🖋️ المعلومات والصور
        </button>
        <button className={`adm-sub-tab ${subTab === 'timeline' ? 'active' : ''}`} onClick={() => setSubTab('timeline')}>
          📅 المسيرة الأدبية ({timeline.length})
        </button>
        <button className={`adm-sub-tab ${subTab === 'memberships' ? 'active' : ''}`} onClick={() => setSubTab('memberships')}>
          🏛️ العضويات ({memberships.length})
        </button>
      </div>

      {subTab === 'info' && (
        <div className="adm-form-card">
          <h3>معلومات الشاعر</h3>

          <div className="adm-form-grid">
            <div className="adm-field">
              <label>اسم الشاعر</label>
              <input value={form.poet_name || ''} onChange={e => set('poet_name', e.target.value)} />
            </div>
            <div className="adm-field">
              <label>اللقب</label>
              <input value={form.poet_title || ''} onChange={e => set('poet_title', e.target.value)} />
            </div>
            <div className="adm-field" style={{ gridColumn: 'span 2' }}>
              <label>اللقب بالإنجليزية</label>
              <input value={form.poet_title_en || ''} onChange={e => set('poet_title_en', e.target.value)} dir="ltr" />
            </div>
          </div>

          <div className="adm-field">
            <label>صورة الصفحة الرئيسية (Hero) {uploading && '(جار الرفع...)'}</label>
            <div className="adm-image-row">
              {form.hero_image_url && <img src={form.hero_image_url} alt="Hero" className="adm-cover-preview" />}
              <input type="file" accept="image/*" disabled={uploading} onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const url = await onUploadImage(file, 'hero');
                  if (url) set('hero_image_url', url);
                }
              }} />
            </div>
          </div>

          <div className="adm-field">
            <label>صورة السيرة الذاتية {uploading && '(جار الرفع...)'}</label>
            <div className="adm-image-row">
              {form.about_image_url && <img src={form.about_image_url} alt="About" className="adm-cover-preview" />}
              <input type="file" accept="image/*" disabled={uploading} onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const url = await onUploadImage(file, 'about');
                  if (url) set('about_image_url', url);
                }
              }} />
            </div>
          </div>

          <div className="adm-field">
            <label>فقرات السيرة الذاتية</label>
            {(form.bio_paragraphs || []).map((p, i) => (
              <div key={i} className="adm-array-item">
                <textarea value={p} onChange={e => updateParagraph(i, e.target.value)} rows="3" placeholder={`الفقرة ${i + 1}`} />
                <button className="adm-btn-ghost danger" onClick={() => removeParagraph(i)} title="حذف">🗑️</button>
              </div>
            ))}
            <button className="adm-btn-ghost" onClick={addParagraph}>＋ إضافة فقرة</button>
          </div>

          <div className="adm-field">
            <label>الاقتباسات المتحركة (في الصفحة الرئيسية)</label>
            {(form.hero_quotes || []).map((q, i) => (
              <div key={i} className="adm-array-item">
                <input value={q} onChange={e => updateQuote(i, e.target.value)} placeholder={`اقتباس ${i + 1}`} />
                <button className="adm-btn-ghost danger" onClick={() => removeQuote(i)} title="حذف">🗑️</button>
              </div>
            ))}
            <button className="adm-btn-ghost" onClick={addQuote}>＋ إضافة اقتباس</button>
          </div>

          <div className="adm-form-actions">
            <button className="adm-btn-gold" onClick={() => onSaveContent(form)}>حفظ المحتوى</button>
          </div>
        </div>
      )}

      {subTab === 'timeline' && (
        <div>
          <div className="adm-header-row">
            <h3 style={{ margin: 0 }}>المسيرة الأدبية</h3>
            <button className="adm-btn-gold" onClick={() => setEditingTimeItem({ year: '', event: '', sort_order: timeline.length + 1 })}>＋ إضافة حدث</button>
          </div>

          {editingTimeItem && (
            <div className="adm-form-card">
              <h3>{editingTimeItem.id ? 'تعديل الحدث' : 'حدث جديد'}</h3>
              <div className="adm-form-grid">
                <div className="adm-field">
                  <label>السنة *</label>
                  <input value={editingTimeItem.year || ''} onChange={e => setEditingTimeItem(prev => ({ ...prev, year: e.target.value }))} placeholder="مثال: 1990" />
                </div>
                <div className="adm-field">
                  <label>الترتيب</label>
                  <input type="number" value={editingTimeItem.sort_order || ''} onChange={e => setEditingTimeItem(prev => ({ ...prev, sort_order: e.target.value }))} />
                </div>
              </div>
              <div className="adm-field">
                <label>الحدث *</label>
                <textarea value={editingTimeItem.event || ''} onChange={e => setEditingTimeItem(prev => ({ ...prev, event: e.target.value }))} rows="2" placeholder="وصف الحدث..." />
              </div>
              <div className="adm-form-actions">
                <button className="adm-btn-gold" onClick={() => { onSaveTimeline(editingTimeItem); setEditingTimeItem(null); }}>حفظ</button>
                <button className="adm-btn-ghost" onClick={() => setEditingTimeItem(null)}>إلغاء</button>
              </div>
            </div>
          )}

          <div className="adm-list">
            {timeline.map(t => (
              <div key={t.id} className="adm-row">
                <div className="adm-row-info">
                  <strong>{t.year}</strong>
                  <span>{t.event}</span>
                </div>
                <div className="adm-row-actions">
                  <button onClick={() => setEditingTimeItem(t)}>✏️</button>
                  <button className="danger" onClick={() => onDeleteTimeline(t.id)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === 'memberships' && (
        <div>
          <div className="adm-header-row">
            <h3 style={{ margin: 0 }}>العضويات والمؤسسات</h3>
            <button className="adm-btn-gold" onClick={() => setEditingMemberItem({ title: '', role: '', icon: '📜', sort_order: memberships.length + 1 })}>＋ إضافة عضوية</button>
          </div>

          {editingMemberItem && (
            <div className="adm-form-card">
              <h3>{editingMemberItem.id ? 'تعديل العضوية' : 'عضوية جديدة'}</h3>
              <div className="adm-form-grid">
                <div className="adm-field">
                  <label>اسم المؤسسة *</label>
                  <input value={editingMemberItem.title || ''} onChange={e => setEditingMemberItem(prev => ({ ...prev, title: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label>الدور *</label>
                  <input value={editingMemberItem.role || ''} onChange={e => setEditingMemberItem(prev => ({ ...prev, role: e.target.value }))} placeholder="مثال: رئيس المنتدى" />
                </div>
                <div className="adm-field">
                  <label>الأيقونة</label>
                  <div className="adm-emoji-row">
                    {EMOJI_OPTIONS.map(em => (
                      <button key={em} className={`adm-emoji-btn ${editingMemberItem.icon === em ? 'active' : ''}`} onClick={() => setEditingMemberItem(prev => ({ ...prev, icon: em }))}>{em}</button>
                    ))}
                  </div>
                </div>
                <div className="adm-field">
                  <label>الترتيب</label>
                  <input type="number" value={editingMemberItem.sort_order || ''} onChange={e => setEditingMemberItem(prev => ({ ...prev, sort_order: e.target.value }))} />
                </div>
              </div>
              <div className="adm-form-actions">
                <button className="adm-btn-gold" onClick={() => { onSaveMembership(editingMemberItem); setEditingMemberItem(null); }}>حفظ</button>
                <button className="adm-btn-ghost" onClick={() => setEditingMemberItem(null)}>إلغاء</button>
              </div>
            </div>
          )}

          <div className="adm-list">
            {memberships.map(m => (
              <div key={m.id} className="adm-row">
                <div className="adm-row-info">
                  <strong>{m.icon} {m.title}</strong>
                  <span>{m.role}</span>
                </div>
                <div className="adm-row-actions">
                  <button onClick={() => setEditingMemberItem(m)}>✏️</button>
                  <button className="danger" onClick={() => onDeleteMembership(m.id)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════
   SUB-COMPONENTS — FORMS
   ════════════════════════════════════════════════════ */

function BookForm({ book, onSave, onCancel, onUploadCover, onUploadPdf, uploading }) {
  const [form, setForm] = useState({ ...book });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !form.id) return;
    const url = await onUploadCover(file, form.id);
    if (url) set('cover_url', url);
  };

  const handlePdfChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !form.id) return;
    if (!file.name.endsWith('.pdf')) { alert('يرجى اختيار ملف PDF'); return; }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 50) { alert(`الملف كبير (${sizeMB.toFixed(1)}MB) — الحد الأقصى 50MB`); return; }
    const url = await onUploadPdf(file, form.id);
    if (url) set('pdf_url', url);
  };

  return (
    <div className="adm-form-card">
      <h3>{form.id ? 'تعديل الديوان' : 'ديوان جديد'}</h3>
      <div className="adm-form-grid">
        <div className="adm-field"><label>عنوان الديوان *</label><input value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="مثال: بوح السنابل" /></div>
        <div className="adm-field"><label>سنة الإصدار</label><input type="number" value={form.year || ''} onChange={e => set('year', e.target.value)} placeholder="2024" /></div>
        <div className="adm-field"><label>النوع</label><input value={form.type || ''} onChange={e => set('type', e.target.value)} placeholder="ديوان شعري" /></div>
        <div className="adm-field"><label>الترتيب</label><input type="number" value={form.sort_order || ''} onChange={e => set('sort_order', e.target.value)} /></div>
      </div>
      <div className="adm-field"><label>الوصف</label><textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows="3" placeholder="وصف مختصر عن الديوان..." /></div>
      <div className="adm-field">
        <label>لون التمييز</label>
        <div className="adm-color-row">{ACCENT_COLORS.map(c => (<button key={c.color} className={`adm-color-btn ${form.accent === c.color ? 'active' : ''}`} style={{ background: c.color }} onClick={() => set('accent', c.color)} title={c.name} />))}</div>
      </div>
      {form.id && (
        <div className="adm-field">
          <label>غلاف الديوان {uploading && '(جار الرفع...)'}</label>
          {form.cover_url && <img src={form.cover_url} alt="غلاف" className="adm-cover-preview" />}
          <input type="file" accept="image/*" onChange={handleCoverChange} disabled={uploading} />
        </div>
      )}
      {form.id && (
        <div className="adm-field">
          <label>ملفات PDF الديوان {uploading && '(جار الرفع...)'}</label>
          {(form.pdf_parts || []).length > 0 && (
            <div className="adm-pdf-parts-list">
              {(form.pdf_parts || []).map((part, idx) => (
                <div key={idx} className="adm-pdf-status">
                  <span>📄</span>
                  <input value={part.name || ''} onChange={(e) => { const updated = [...(form.pdf_parts || [])]; updated[idx] = { ...updated[idx], name: e.target.value }; set('pdf_parts', updated); }} placeholder="اسم الجزء" style={{ flex: 1, padding: '.3rem .5rem', fontSize: '.82rem', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: '6px', color: '#E8DCC8' }} />
                  <button type="button" className="adm-btn-ghost" style={{ padding: '.25rem .6rem', fontSize: '.75rem', color: '#C4A44A', whiteSpace: 'nowrap' }} onClick={async () => { const path = decodeURIComponent((part.url || '').split('/pdfs/')[1] || '').split('?')[0]; const { data, error } = await supabase.storage.from('pdfs').createSignedUrl(path, 60); if (error || !data?.signedUrl) { alert('تعذّر فتح الملف'); return; } window.open(data.signedUrl, '_blank', 'noopener,noreferrer'); }}>معاينة</button>
                  <button type="button" className="adm-btn-ghost" style={{ padding: '.25rem .6rem', fontSize: '.72rem' }} onClick={() => { const updated = (form.pdf_parts || []).filter((_, i) => i !== idx); set('pdf_parts', updated); const fileName = part.url.split('/').pop(); supabase.storage.from('pdfs').remove([fileName]); }}>🗑️</button>
                </div>
              ))}
            </div>
          )}
          <div className="adm-pdf-add-row">
            <input id="pdf-part-name" placeholder="اسم الجزء (مثال: الجزء الأول)" style={{ flex: 1, padding: '.5rem .75rem', fontSize: '.85rem', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: '8px', color: '#E8DCC8' }} />
            <label className="adm-btn-ghost" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '.3rem', fontSize: '.82rem' }}>
              📁 اختيار ملف
              <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={uploading} onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const nameInput = document.getElementById('pdf-part-name'); const partName = nameInput?.value?.trim() || `جزء ${(form.pdf_parts || []).length + 1}`; const url = await onUploadPdf(file, `${form.id}_part${(form.pdf_parts || []).length + 1}`); if (url) { const updated = [...(form.pdf_parts || []), { name: partName, url }]; set('pdf_parts', updated); if (nameInput) nameInput.value = ''; } e.target.value = ''; }} />
            </label>
          </div>
          <p className="adm-hint-text">يمكنك إضافة أكثر من جزء · أدخل اسم الجزء ثم اختر الملف</p>
        </div>
      )}
      <div className="adm-field">
        <label>رابط شراء الديوان (WhatsApp أو أي رابط)</label>
        <input value={form.buy_link || ''} onChange={e => set('buy_link', e.target.value)} placeholder="مثال: https://wa.me/961XXXXXXXX?text=أريد شراء ديوان..." dir="ltr" />
        <p className="adm-hint-text">يظهر كزرّ "اطلب نسختك" في صفحة الديوان · اتركه فارغاً لإخفائه</p>
      </div>
      <div className="adm-field">
        <label>عدد صفحات المعاينة للزوار</label>
        <input type="number" min="1" max="60" value={form.preview_pages ?? 10} onChange={e => set('preview_pages', e.target.value)} dir="ltr" />
        <p className="adm-hint-text">عدد الصفحات الأولى من الجزء الأول التي يمكن للزائر تصفّحها (1–60)</p>
      </div>
      <div className="adm-field"><label><input type="checkbox" checked={form.allow_full_read === true} onChange={e => set('allow_full_read', e.target.checked)} /> السماح بقراءة الديوان كاملاً (بدون تنزيل أو طباعة)</label></div>
      <div className="adm-field"><label><input type="checkbox" checked={form.is_latest || false} onChange={e => set('is_latest', e.target.checked)} /> أحدث إصدار</label></div>
      <div className="adm-form-actions">
        <button className="adm-btn-gold" onClick={() => onSave(form)}>حفظ</button>
        <button className="adm-btn-ghost" onClick={onCancel}>إلغاء</button>
      </div>
      {!form.id && <p className="adm-hint-text">احفظ الديوان أولاً ثم يمكنك رفع الغلاف</p>}
    </div>
  );
}

function PoemForm({ poem, books, onSave, onCancel }) {
  const [form, setForm] = useState({ ...poem });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  return (
    <div className="adm-form-card">
      <h3>{form.id ? 'تعديل القصيدة' : 'قصيدة جديدة'}</h3>
      <div className="adm-form-grid">
        <div className="adm-field"><label>عنوان القصيدة *</label><input value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label>الديوان *</label><select value={form.book_id || ''} onChange={e => set('book_id', e.target.value)}><option value="">— اختر —</option>{books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}</select></div>
        <div className="adm-field"><label>التصنيف</label><select value={form.category || 'غزل'} onChange={e => set('category', e.target.value)}>{POEM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        <div className="adm-field"><label>الترتيب</label><input type="number" value={form.sort_order || ''} onChange={e => set('sort_order', e.target.value)} /></div>
      </div>
      <div className="adm-field"><label>الأبيات * (كل سطر = بيت)</label><textarea className="adm-verses" value={form.verses || ''} onChange={e => set('verses', e.target.value)} rows="10" placeholder="اكتب أو الصق الأبيات هنا..." /></div>
      <div className="adm-form-actions"><button className="adm-btn-gold" onClick={() => onSave(form)}>حفظ</button><button className="adm-btn-ghost" onClick={onCancel}>إلغاء</button></div>
    </div>
  );
}

function MunfaridaForm({ data, onSave, onCancel }) {
  const [form, setForm] = useState({ ...data });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  return (
    <div className="adm-form-card">
      <h3>{form.id ? 'تعديل المنفردة' : 'منفردة جديدة'}</h3>
      <div className="adm-form-grid">
        <div className="adm-field"><label>عنوان القصيدة *</label><input value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label>التصنيف</label><select value={form.category || 'غزل'} onChange={e => set('category', e.target.value)}>{POEM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
      </div>
      <div className="adm-field"><label>الأبيات * (كل سطر = بيت)</label><textarea className="adm-verses" value={form.verses || ''} onChange={e => set('verses', e.target.value)} rows="10" /></div>
      <div className="adm-form-actions"><button className="adm-btn-gold" onClick={() => onSave(form)}>حفظ</button><button className="adm-btn-ghost" onClick={onCancel}>إلغاء</button></div>
    </div>
  );
}

function VideoForm({ data, onSave, onCancel, onUploadVideo, uploading, getStorageUsed }) {
  const [form, setForm] = useState({ ...data, video_type: data.video_type || 'youtube' });
  const [storageUsed, setStorageUsed] = useState(0);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const STORAGE_LIMIT_MB = 100;

  useEffect(() => {
    if (form.video_type === 'uploaded') {
      getStorageUsed().then(bytes => setStorageUsed(bytes / (1024 * 1024)));
    }
  }, [form.video_type, getStorageUsed]);

  const handleUrlInput = (val) => {
    const ytMatch = val.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) { setForm(prev => ({ ...prev, video_type: 'youtube', youtube_id: ytMatch[1], video_url: val })); return; }
    if (val.includes('instagram.com')) { setForm(prev => ({ ...prev, video_type: 'instagram', video_url: val, youtube_id: null })); return; }
    if (val.includes('facebook.com') || val.includes('fb.watch')) { setForm(prev => ({ ...prev, video_type: 'facebook', video_url: val, youtube_id: null })); return; }
    if (val.includes('tiktok.com')) { setForm(prev => ({ ...prev, video_type: 'tiktok', video_url: val, youtube_id: null })); return; }
    if (/^[a-zA-Z0-9_-]{11}$/.test(val)) { setForm(prev => ({ ...prev, video_type: 'youtube', youtube_id: val, video_url: null })); return; }
    setForm(prev => ({ ...prev, video_url: val }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 100) { alert(`الملف كبير جداً (${sizeMB.toFixed(1)}MB)`); return; }
    const url = await onUploadVideo(file);
    if (url) { setForm(prev => ({ ...prev, video_type: 'uploaded', video_url: url, youtube_id: null })); setStorageUsed(prev => prev + sizeMB); }
  };

  const SOURCE_TYPES = [{ id: 'link', label: 'رابط (YouTube / Instagram / Facebook)' }, { id: 'uploaded', label: 'رفع فيديو من الجهاز' }];
  const [sourceMode, setSourceMode] = useState(form.video_type === 'uploaded' ? 'uploaded' : 'link');

  const handleSave = () => {
    if (!form.title) { alert('يرجى إدخال العنوان'); return; }
    if (sourceMode === 'link' && !form.youtube_id && !form.video_url) { alert('يرجى إدخال رابط الفيديو'); return; }
    if (sourceMode === 'uploaded' && !form.video_url) { alert('يرجى رفع الفيديو أولاً'); return; }
    onSave(form);
  };

  return (
    <div className="adm-form-card">
      <h3>{form.id ? 'تعديل الفيديو' : 'فيديو جديد'}</h3>
      <div className="adm-field" style={{ marginBottom: '1rem' }}>
        <label>مصدر الفيديو</label>
        <div className="adm-source-tabs">{SOURCE_TYPES.map(s => (<button key={s.id} className={`adm-source-tab ${sourceMode === s.id ? 'active' : ''}`} onClick={() => setSourceMode(s.id)} type="button">{s.id === 'link' ? '🔗' : '📁'} {s.label}</button>))}</div>
      </div>
      <div className="adm-form-grid">
        <div className="adm-field"><label>العنوان *</label><input value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
        {sourceMode === 'link' ? (
          <div className="adm-field">
            <label>رابط الفيديو *</label>
            <input value={form.video_url || form.youtube_id || ''} onChange={e => handleUrlInput(e.target.value)} placeholder="الصق أي رابط فيديو هنا" dir="ltr" />
            {form.video_type && form.video_type !== 'uploaded' && (
              <span className="adm-detected-type">
                {form.video_type === 'youtube' && '▶ YouTube'}{form.video_type === 'instagram' && '📷 Instagram'}{form.video_type === 'facebook' && '📘 Facebook'}{form.video_type === 'tiktok' && '🎵 TikTok'}
              </span>
            )}
          </div>
        ) : (
          <div className="adm-field">
            <label>رفع فيديو (حد أقصى 50MB)</label>
            <input type="file" accept="video/*" onChange={handleFileUpload} disabled={uploading} />
            {uploading && <span className="adm-uploading">جار الرفع...</span>}
            <div className="adm-storage-meter"><div className="adm-storage-bar"><div className="adm-storage-fill" style={{ width: `${Math.min((storageUsed / STORAGE_LIMIT_MB) * 100, 100)}%` }} /></div><span className="adm-storage-text">{storageUsed.toFixed(1)}MB / {STORAGE_LIMIT_MB}MB</span></div>
          </div>
        )}
        <div className="adm-field"><label>التصنيف</label><select value={form.category || 'recitation'} onChange={e => set('category', e.target.value)}>{VIDEO_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
        <div className="adm-field"><label>المدة</label><input value={form.duration || ''} onChange={e => set('duration', e.target.value)} placeholder="5:32" dir="ltr" /></div>
        <div className="adm-field"><label>السنة</label><input type="number" value={form.year || ''} onChange={e => set('year', e.target.value)} /></div>
        <div className="adm-field"><label>الترتيب</label><input type="number" value={form.sort_order || ''} onChange={e => set('sort_order', e.target.value)} /></div>
      </div>
      <div className="adm-field"><label>الوصف</label><textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows="2" /></div>
      <div className="adm-field"><label><input type="checkbox" checked={form.is_featured || false} onChange={e => set('is_featured', e.target.checked)} /> فيديو مميّز</label></div>
      {form.video_type === 'youtube' && form.youtube_id && <div className="adm-video-preview"><img src={`https://img.youtube.com/vi/${form.youtube_id}/mqdefault.jpg`} alt="معاينة" /></div>}
      {form.video_type === 'uploaded' && form.video_url && <div className="adm-video-preview"><video src={form.video_url} controls style={{ width: '100%', maxWidth: 320, borderRadius: 8 }} /></div>}
      <div className="adm-form-actions"><button className="adm-btn-gold" onClick={handleSave} disabled={uploading}>{uploading ? 'جار الرفع...' : 'حفظ'}</button><button className="adm-btn-ghost" onClick={onCancel}>إلغاء</button></div>
    </div>
  );
}

const EVENT_TYPES_LIST = ['أمسية شعرية', 'مؤتمر أدبي', 'مهرجان دولي', 'ندوة', 'توقيع ديوان', 'لقاء تلفزيوني'];

function EventForm({ data, onSave, onCancel, onUploadImage, uploading }) {
  const [form, setForm] = useState({ ...data });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !form.id) return;
    const url = await onUploadImage(file, form.id);
    if (url) set('image_url', url);
  };

  return (
    <div className="adm-form-card">
      <h3>{form.id ? 'تعديل الفعالية' : 'فعالية جديدة'}</h3>
      <div className="adm-form-grid">
        <div className="adm-field"><label>عنوان الفعالية *</label><input value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="مثال: أمسية شعرية في بعقلين" /></div>
        <div className="adm-field"><label>التاريخ</label><input type="date" value={form.event_date || ''} onChange={e => set('event_date', e.target.value)} dir="ltr" /></div>
        <div className="adm-field"><label>نوع الفعالية</label><select value={form.event_type || 'أمسية شعرية'} onChange={e => set('event_type', e.target.value)}>{EVENT_TYPES_LIST.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
        <div className="adm-field"><label>المكان</label><input value={form.location || ''} onChange={e => set('location', e.target.value)} placeholder="مثال: المركز الثقافي" /></div>
        <div className="adm-field"><label>المدينة</label><input value={form.city || ''} onChange={e => set('city', e.target.value)} placeholder="مثال: بيروت" /></div>
        <div className="adm-field"><label>البلد</label><input value={form.country || 'لبنان'} onChange={e => set('country', e.target.value)} /></div>
      </div>
      <div className="adm-field"><label>الوصف</label><textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows="3" placeholder="وصف مختصر عن الفعالية..." /></div>
      <div className="adm-field"><label><input type="checkbox" checked={form.is_upcoming || false} onChange={e => set('is_upcoming', e.target.checked)} /> فعالية قادمة</label></div>
      {form.id && (
        <div className="adm-field">
          <label>صورة الفعالية {uploading && '(جار الرفع...)'}</label>
          {form.image_url && <img src={form.image_url} alt="صورة" className="adm-cover-preview" style={{ width: 160, height: 100, objectFit: 'cover' }} />}
          <input type="file" accept="image/*" onChange={handleImageChange} disabled={uploading} />
        </div>
      )}
      <div className="adm-form-actions"><button className="adm-btn-gold" onClick={() => onSave(form)}>حفظ</button><button className="adm-btn-ghost" onClick={onCancel}>إلغاء</button></div>
      {!form.id && <p className="adm-hint-text">احفظ الفعالية أولاً ثم يمكنك رفع الصورة</p>}
    </div>
  );
}